/**
 * Le chiamate al backend, una funzione per endpoint.
 *
 * I componenti non compongono URL a mano: chiedono a queste funzioni. Così
 * quando un percorso cambia si corregge in un posto solo.
 */
import { api, query } from './client.ts';
import type {
  Allegato,
  Categoria,
  Avvisabile,
  AvvisoFermo,
  ImpostazioniAllegati,
  StatoSpazio,
  RegoleAllegati,
  AnteprimaAvviso,
  Aspetto,
  AspettoSalvato,
  ConfigurazioneSmtp,
  DatiScadenza,
  Elenco,
  EsitoAnteprima,
  EsitoEliminazione,
  EsitoNotifiche,
  EsitoProvaSmtp,
  FiltriAttivita,
  FiltriScadenze,
  ImpattoEliminazione,
  Riepilogo,
  Ruolo,
  Scadenza,
  SceltaCollegate,
  SicurezzaSmtp,
  Soggetto,
  StatoAggiornamenti,
  StoricoAggiornamenti,
  TipoScadenza,
  Utente,
  VoceAttivita,
  VoceNotifica,
  VoceStorico,
} from './tipi-dati.ts';

// --- Autenticazione ----------------------------------------------------------

export const autenticazione = {
  accedi: (email: string, password: string) =>
    api.post<Utente>('/auth/login', { email, password }),

  esci: () => api.post<{ esito: 'ok' }>('/auth/logout'),

  /** Chi sono. Risponde 401 se la sessione non è valida. */
  io: () => api.get<Utente>('/auth/io'),

  /**
   * Cambia le proprie preferenze.
   *
   * Non passa da `/utenti/:id`: quelle rotte sono riservate agli
   * amministratori, e la ricezione degli avvisi riguarda la propria casella —
   * un operatore deve poterla spegnere da sé. Il destinatario non è un
   * parametro: è sempre chi chiede.
   */
  cambiaPreferenze: (preferenze: { riceveNotifiche: boolean }) =>
    api.patch<Utente>('/auth/io', preferenze),

  cambiaPassword: (passwordAttuale: string, passwordNuova: string) =>
    api.post<{ esito: 'ok' }>('/auth/cambia-password', { passwordAttuale, passwordNuova }),
};

// --- Scadenze ----------------------------------------------------------------

export const scadenze = {
  riepilogo: () => api.get<Riepilogo>('/scadenze/riepilogo'),

  elenca: (filtri: FiltriScadenze = {}) =>
    api.get<Elenco<Scadenza>>(`/scadenze${query({ ...filtri })}`),

  /**
   * Le prossime scadenze aperte, ordinate per data.
   * `stato=APERTA` esclude completate e annullate ma tiene insieme scadute e
   * in scadenza, che è quello che il cruscotto deve mostrare.
   */
  prossime: (limite = 10) => api.get<Elenco<Scadenza>>(`/scadenze${query({ stato: 'APERTA', limite })}`),

  ottieni: (id: number) => api.get<Scadenza>(`/scadenze/${id}`),

  /** Chi ha creato, modificato, completato o annullato. Dal registro operazioni. */
  storico: (id: number) => api.get<{ voci: VoceStorico[] }>(`/scadenze/${id}/storico`),

  crea: (dati: DatiScadenza) => api.post<Scadenza>('/scadenze', dati),

  modifica: (id: number, dati: Partial<DatiScadenza>) =>
    api.patch<Scadenza>(`/scadenze/${id}`, dati),

  completa: (
    id: number,
    dati: {
      dataCompletamento?: string;
      dataProssimaScadenza?: string | null;
      senzaRinnovo?: boolean;
      note?: string;
    },
  ) => api.post<{ completata: Scadenza; rinnovo: Scadenza | null }>(`/scadenze/${id}/completa`, dati),

  annulla: (id: number, motivo: string) =>
    api.post<Scadenza>(`/scadenze/${id}/annulla`, { motivo }),

  /**
   * Riapre una completata per errore, cancellando il rinnovo generato.
   * Risponde 409 se quel rinnovo è già stato lavorato.
   */
  riapri: (id: number) =>
    api.post<{
      riaperta: Scadenza;
      rinnovoRimosso: { id: number; dataScadenza: string } | null;
    }>(`/scadenze/${id}/riapri`),

  /**
   * Cosa si perde cancellando questa scadenza. Non modifica niente: si può
   * chiamare quante volte si vuole, ed è quello che il dialogo di conferma
   * interroga **prima** di mostrare il pulsante rosso.
   */
  impattoEliminazione: (id: number) =>
    api.get<ImpattoEliminazione>(`/scadenze/${id}/impatto-eliminazione`),

  /**
   * ⚠️ Cancella davvero, e non si può annullare. Nel lavoro di tutti i giorni
   * la via giusta è `annulla`, che lascia la scadenza nello storico col motivo.
   * Riservata agli amministratori.
   */
  elimina: (id: number) => api.delete<EsitoEliminazione>(`/scadenze/${id}`),
};

// --- Registro delle attività -------------------------------------------------

/**
 * Il registro delle operazioni, in due rotte e non in una.
 *
 * ⚠️ La differenza fra `mie` e `tutte` **non è un parametro**: sono due
 * indirizzi con due guardie diverse, `autenticato` la prima e
 * `soloAmministratore` la seconda. Se l'utente da mostrare fosse un parametro
 * che il backend sovrascrive per i non amministratori, basterebbe una svista
 * perché a chiunque comparissero le attività di tutti. Con due rotte la
 * guardia è dichiarata nel posto dove si sbaglia meno.
 *
 * Per la stessa ragione `mie` **non accetta** `utenteId`: non c'è nessun modo
 * di chiedere da lì le attività di un collega, nemmeno scrivendolo a mano
 * nell'indirizzo.
 */
export const attivita = {
  /** Quello che ho fatto io. Per chiunque sia collegato. */
  mie: (paginazione: Pick<FiltriAttivita, 'pagina' | 'perPagina'> = {}) =>
    api.get<Elenco<VoceAttivita>>(`/attivita/mie${query({ ...paginazione })}`),

  /** Tutto l'ufficio. Il backend risponde 403 a chi non è amministratore. */
  tutte: (filtri: FiltriAttivita = {}) =>
    api.get<Elenco<VoceAttivita>>(`/attivita${query({ ...filtri })}`),
};

// --- Anagrafiche -------------------------------------------------------------

export interface DatiSoggetto {
  tipo: string;
  codice: string | null;
  denominazione: string;
  reparto: string | null;
  email: string | null;
  note: string | null;
}

export const soggetti = {
  elenca: (filtri: { tipo?: string; attivo?: string; cerca?: string; limite?: number } = {}) =>
    api.get<Elenco<Soggetto>>(`/soggetti${query({ ...filtri })}`),
  ottieni: (id: number) => api.get<Soggetto>(`/soggetti/${id}`),
  crea: (dati: DatiSoggetto) => api.post<Soggetto>('/soggetti', dati),
  modifica: (id: number, dati: Partial<DatiSoggetto>) =>
    api.patch<Soggetto>(`/soggetti/${id}`, dati),
  /** Non cancella: le scadenze passate devono restare leggibili. */
  disattiva: (id: number) => api.post<Soggetto>(`/soggetti/${id}/disattiva`),
  riattiva: (id: number) => api.post<Soggetto>(`/soggetti/${id}/riattiva`),

  /**
   * Cosa si perde cancellando, **per il ramo che si sta guardando**.
   *
   * Senza `collegate` il backend descrive `SCOLLEGA`, il meno distruttivo: è
   * il ramo giusto da mostrare a chi ha appena aperto il dialogo e non ha
   * ancora scelto niente. Va richiamata a ogni cambio di scelta, altrimenti il
   * dialogo elencherebbe le conseguenze di un ramo mentre sta per eseguirne un
   * altro.
   */
  impattoEliminazione: (id: number, collegate?: SceltaCollegate) =>
    api.get<ImpattoEliminazione>(`/soggetti/${id}/impatto-eliminazione${query({ collegate })}`),

  /**
   * ⚠️ Cancella davvero, e non si può annullare.
   *
   * `collegate` dice che fine fanno le scadenze del soggetto: `ELIMINA` se le
   * si porta via con lui, `SCOLLEGA` se restano nello storico orfane, col nome
   * congelato sulla riga. **È obbligatorio quando ce n'è almeno una**: senza,
   * il backend rifiuta con 400, così il ramo distruttivo non può partire per
   * omissione.
   *
   * Per un dipendente che se ne va o un macchinario dismesso la risposta giusta
   * resta comunque `disattiva`.
   */
  elimina: (id: number, collegate?: SceltaCollegate) =>
    api.delete<EsitoEliminazione>(
      `/soggetti/${id}`,
      collegate === undefined ? undefined : { collegate },
    ),
};

// --- Categorie ---------------------------------------------------------------

export const categorie = {
  elenca: (filtri: { attiva?: string; limite?: number } = {}) =>
    api.get<Elenco<Categoria>>(`/categorie${query({ ...filtri })}`),

  ottieni: (id: number) => api.get<Categoria>(`/categorie/${id}`),

  crea: (dati: { nome: string; descrizione: string | null }) =>
    api.post<Categoria>('/categorie', dati),

  modifica: (id: number, dati: { nome?: string; descrizione?: string | null }) =>
    api.patch<Categoria>(`/categorie/${id}`, dati),

  disattiva: (id: number) => api.post<Categoria>(`/categorie/${id}/disattiva`),
  riattiva: (id: number) => api.post<Categoria>(`/categorie/${id}/riattiva`),

  /**
   * Cosa comporta cancellarla. Nessun ramo da scegliere: i tipi restano
   * sempre, e perdono la categoria.
   */
  impattoEliminazione: (id: number) =>
    api.get<ImpattoEliminazione>(`/categorie/${id}/impatto-eliminazione`),

  elimina: (id: number) => api.delete<EsitoEliminazione>(`/categorie/${id}`),
};

export const tipiScadenza = {
  elenca: (filtri: { categoriaId?: number; attivo?: string; limite?: number } = {}) =>
    api.get<Elenco<TipoScadenza>>(`/tipi${query({ ...filtri })}`),
  ottieni: (id: number) => api.get<TipoScadenza>(`/tipi/${id}`),

  /**
   * Creazione rapida dal form di una scadenza, quando il tipo manca.
   * Riservata agli amministratori: il backend risponde 403 agli operatori.
   */
  crea: (dati: DatiTipo) => api.post<TipoScadenza>('/tipi', dati),
  modifica: (id: number, dati: Partial<DatiTipo>) =>
    api.patch<TipoScadenza>(`/tipi/${id}`, dati),
  disattiva: (id: number) => api.post<TipoScadenza>(`/tipi/${id}/disattiva`),
  riattiva: (id: number) => api.post<TipoScadenza>(`/tipi/${id}/riattiva`),

  /** Come per i soggetti: senza `collegate` descrive il ramo `SCOLLEGA`. */
  impattoEliminazione: (id: number, collegate?: SceltaCollegate) =>
    api.get<ImpattoEliminazione>(`/tipi/${id}/impatto-eliminazione${query({ collegate })}`),

  /**
   * ⚠️ Cancella davvero. Come i soggetti: `collegate` decide se le scadenze di
   * questo tipo spariscono con lui o restano nello storico orfane, col nome del
   * tipo congelato sulla riga.
   */
  elimina: (id: number, collegate?: SceltaCollegate) =>
    api.delete<EsitoEliminazione>(
      `/tipi/${id}`,
      collegate === undefined ? undefined : { collegate },
    ),
};

export interface DatiTipo {
  categoriaId: number;
  nome: string;
  /** `null` = una tantum, non genera rinnovi. */
  periodicitaMesi: number | null;
  preavvisoGiorni: number;
  note: string | null;
  /** Gli id dei destinatari predefiniti: le scadenze di questo tipo li ereditano. */
  destinatari?: number[];
}

// --- Utenti ------------------------------------------------------------------

export interface DatiNuovoUtente {
  email: string;
  nome: string;
  ruolo: Ruolo;
  riceveNotifiche: boolean;
  /** Vale fino al primo accesso: poi il collega è obbligato a cambiarla. */
  passwordProvvisoria: string;
}

export type DatiModificaUtente = Partial<Pick<DatiNuovoUtente, 'email' | 'nome' | 'ruolo' | 'riceveNotifiche'>>;

export const utenti = {
  elenca: (includiDisattivati = false) =>
    api.get<Elenco<Utente>>(`/utenti${query({ includiDisattivati: String(includiDisattivati) })}`),

  crea: (dati: DatiNuovoUtente) => api.post<Utente>('/utenti', dati),

  /** Non tocca la password: per quella c'è `reimpostaPassword`. */
  modifica: (id: number, dati: DatiModificaUtente) => api.patch<Utente>(`/utenti/${id}`, dati),

  /** Non cancella: lo storico deve continuare a dire chi ha fatto cosa. */
  disattiva: (id: number) => api.post<Utente>(`/utenti/${id}/disattiva`),
  riattiva: (id: number) => api.post<Utente>(`/utenti/${id}/riattiva`),

  reimpostaPassword: (id: number, passwordProvvisoria: string) =>
    api.post<{ esito: 'ok' }>(`/utenti/${id}/reimposta-password`, { passwordProvvisoria }),

  /**
   * Rifiuta per le stesse due ragioni della disattivazione — non puoi eliminare
   * te stesso, non si resta senza amministratori attivi — e in quel caso il
   * `motivo` lo spiega. È il motivo per cui il pulsante si può mostrare anche
   * su sé stessi: il dialogo diventa la spiegazione del divieto, non un errore.
   */
  impattoEliminazione: (id: number) =>
    api.get<ImpattoEliminazione>(`/utenti/${id}/impatto-eliminazione`),

  /**
   * ⚠️ Cancella davvero. Nella maggior parte dei casi la risposta giusta è
   * `disattiva`: toglie l'accesso e lascia intatto lo storico di chi ha
   * completato cosa. Questa serve per il collega inserito per errore.
   */
  elimina: (id: number) => api.delete<EsitoEliminazione>(`/utenti/${id}`),
};

// --- Server di posta ---------------------------------------------------------

export interface DatiSmtp {
  host: string;
  porta: number;
  sicurezza: SicurezzaSmtp;
  utente: string;
  /**
   * Omessa = "lascia quella che c'è".
   * Serve per correggere porta o mittente senza doversi far ridare la
   * password della casella.
   */
  password?: string;
  mittenteEmail: string;
  mittenteNome: string;
}

export const impostazioni = {
  leggiSmtp: () => api.get<ConfigurazioneSmtp>('/impostazioni/smtp'),

  /** Ogni salvataggio disattiva l'invio finché non si rifà la prova. */
  salvaSmtp: (dati: DatiSmtp) => api.put<ConfigurazioneSmtp>('/impostazioni/smtp', dati),

  /**
   * Invia una mail vera. Solo se arriva a destinazione le notifiche si
   * attivano: un server può autenticare e poi rifiutare il mittente.
   */
  provaSmtp: (destinatario?: string) =>
    api.post<EsitoProvaSmtp>(
      '/impostazioni/smtp/prova',
      destinatario !== undefined ? { destinatario } : {},
    ),
};

// --- Allegati ----------------------------------------------------------------

export const allegati = {
  /** Limiti e formati ammessi. Da chiedere una volta, non a ogni caricamento. */
  regole: () => api.get<RegoleAllegati>('/scadenze/regole-allegati'),

  elenca: (scadenzaId: number) =>
    api.get<Elenco<Allegato>>(`/scadenze/${scadenzaId}/allegati`),

  carica: (scadenzaId: number, file: File) =>
    api.carica<Allegato>(`/scadenze/${scadenzaId}/allegati`, file),

  elimina: (id: number) => api.delete<Allegato>(`/scadenze/allegati/${id}`),

  /**
   * L'indirizzo da cui si apre il file.
   *
   * È un URL normale e non una chiamata `fetch` di proposito: così il file si
   * apre in una scheda del browser, che per un PDF o un'immagine sa fare molto
   * meglio di quanto potremmo fare noi. Passa comunque dalla rotta
   * autenticata, perché il cookie di sessione viaggia con la navigazione.
   */
  urlDi: (id: number) => `/api/scadenze/allegati/${id}`,
};

// --- Utenti selezionabili come destinatari -----------------------------------

/**
 * L'elenco leggero dei colleghi, per il selettore dei destinatari.
 *
 * ⚠️ Non è `/utenti`: quella rotta è per soli amministratori, e il selettore
 * sta nel form delle scadenze, che usano tutti. Espone il minimo per riempire
 * un menu.
 */
export const avvisabili = {
  elenca: () => api.get<Elenco<Avvisabile>>('/utenti/avvisabili'),
};

// --- Impostazioni degli allegati ---------------------------------------------

export const impostazioniAllegati = {
  leggi: () => api.get<ImpostazioniAllegati>('/impostazioni/allegati'),

  salva: (dati: {
    maxMb: number;
    formati: string[];
    maxPerScadenza: number;
    soloAmministratoriEliminano: boolean;
    sogliaSpazioMb: number;
  }) => api.put<ImpostazioniAllegati>('/impostazioni/allegati', dati),

  spazio: () => api.get<StatoSpazio>('/impostazioni/allegati/spazio'),

  pulisciOrfani: () =>
    api.post<{ rimossi: number; byte: number; leggibile: string }>(
      '/impostazioni/allegati/pulizia-orfani',
    ),
};

// --- Notifiche ---------------------------------------------------------------

export const notifiche = {
  /** Fa subito quello che l'app fa da sola ogni giorno. Innocua: non ripete. */
  esegui: () => api.post<EsitoNotifiche>('/notifiche/esegui'),

  storico: (limite = 20) =>
    api.get<Elenco<VoceNotifica>>(`/notifiche/storico${query({ limite })}`),

  /**
   * Com'è fatto un avviso. Non manda niente: si può guardare quanto si vuole.
   *
   * Con `bozza` compone l'esempio con un aspetto **non ancora salvato**, che è
   * ciò che permette di guardare un colore prima di metterlo in vigore. Senza,
   * usa quello salvato.
   */
  anteprima: (bozza?: Aspetto) =>
    bozza === undefined
      ? api.get<AnteprimaAvviso>('/notifiche/anteprima')
      : api.post<AnteprimaAvviso>('/notifiche/anteprima', { bozza }),

  /**
   * Manda l'avviso d'esempio alla propria casella. Il destinatario lo decide il
   * backend — è sempre chi lo chiede — così questo pulsante non può diventare
   * un modo per spedire posta a un indirizzo qualsiasi.
   *
   * ⚠️ Si passa la bozza aperta nell'editor: la prova deve mostrare **quello
   * che si sta guardando**, non quello che è salvato. Una prova che mente è
   * peggio di nessuna prova, perché sembra funzionare.
   */
  inviaAnteprima: (bozza?: Aspetto) =>
    api.post<EsitoAnteprima>('/notifiche/anteprima/invia', { bozza }),

  // --- L'aspetto modificabile (punto 6c) ---

  aspetto: () => api.get<AspettoSalvato>('/notifiche/aspetto'),

  salvaAspetto: (dati: Aspetto) => api.put<AspettoSalvato>('/notifiche/aspetto', dati),

  /** Rimette i valori di fabbrica. Resta registrato chi l'ha fatto e quando. */
  ripristinaAspetto: () => api.post<AspettoSalvato>('/notifiche/aspetto/ripristina'),
};

// --- Aggiornamenti -------------------------------------------------------

export const aggiornamenti = {
  stato: () => api.get<StatoAggiornamenti>('/aggiornamenti'),

  /**
   * Lo stesso stato, ma per il **riaggiornamento periodico** della schermata
   * Aggiornamenti, che si rilegge ogni minuto da solo.
   *
   * Sono due funzioni e non una perché i due usi non vogliono la stessa cosa
   * da un 401. Il primo caricamento nasce dalla navigazione — chi è appena
   * arrivato su una schermata con la sessione morta **deve** essere portato
   * all'accesso, non lasciato davanti a un caricamento infinito. Il giro
   * periodico no: chi è rimasto lì a guardare l'avanzamento di un
   * aggiornamento che ha appena lanciato non deve ritrovarsi al login perché
   * nel frattempo la sessione ha compiuto dodici ore.
   *
   * ⚠️ È esattamente lo stesso difetto della striscia, nello stesso modulo, e
   * la prima correzione l'aveva lasciato aperto: la revisione l'ha trovato qui.
   */
  statoDiFondo: () => api.getDiFondo<StatoAggiornamenti>('/aggiornamenti'),

  /**
   * L'unica chiamata di questo gruppo che **non** richiede di essere
   * amministratori: serve alla striscia di preavviso, che deve arrivare a chi
   * sta lavorando. Viene chiamata ogni 30 secondi da tutte le schede aperte.
   *
   * ⚠️ Passa da `getDiFondo`, non da `get`, e la differenza non è cosmetica:
   * è l'unica chiamata dell'applicazione che parte **da sola**, quindi è
   * l'unica che può incontrare la scadenza della sessione senza che nessuno
   * stia aspettando niente. Col `get` normale, alle dodici ore l'utente
   * verrebbe riportato all'accesso entro mezzo minuto, portandosi via il
   * modulo che aveva aperto. Il perché per esteso sta su `getDiFondo`.
   */
  avviso: () => api.getDiFondo<AvvisoFermo>('/aggiornamenti/avviso'),

  /**
   * ⚠️ Questa chiamata **ammazza il server che le risponde**: passato il
   * preavviso, l'applicazione si ferma per aggiornarsi. Chi la chiama non deve
   * aspettarsi di poter fare altre chiamate dopo il fermo.
   *
   * `preavvisoSecondi` è quanto l'host deve aspettare, a immagine già
   * costruita, prima di fermare: 0, 300 o 900. Lo sceglie chi preme, perché è
   * l'unico a sapere se è mercoledì alle 11 o domenica sera.
   */
  /**
   * Lo storico dei tentativi. `get` e non `getDiFondo`: questa parte da una
   * navigazione, e chi ci arriva con la sessione morta deve essere portato
   * all'accesso invece di restare davanti a un caricamento infinito. La
   * schermata non si riaggiorna da sola — è una cronaca, non un cruscotto.
   */
  storico: () => api.get<StoricoAggiornamenti>('/aggiornamenti/storico'),

  /**
   * Lo stesso storico, per la rilettura che parte **da sola** quando arriva un
   * tentativo nuovo. Stessa ragione di `statoDiFondo`, nello stesso modulo: chi
   * è rimasto a guardare un aggiornamento che ha appena lanciato non deve
   * ritrovarsi all'accesso perché nel frattempo la sessione ha compiuto dodici
   * ore.
   */
  storicoDiFondo: () => api.getDiFondo<StoricoAggiornamenti>('/aggiornamenti/storico'),

  installa: (versione: string, preavvisoSecondi: 0 | 300 | 900) =>
    api.post<{ esito: 'richiesta'; versione: string }>('/aggiornamenti/installa', {
      versione,
      preavvisoSecondi,
    }),
};
